/**
 * SignatureUploadDialog - Dialog for uploading/editing signatures
 */

import type { SignaturePublic, UpdateSignatureRequest } from '@garden-platform/shared';
import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useSignatureImageUrl, useUpdateSignature, useUploadSignature } from '../../hooks';

interface FormData {
  name: string;
  title: string;
  isActive: boolean;
}

const defaultFormData: FormData = {
  name: '',
  title: '',
  isActive: true,
};

export interface SignatureUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  signature?: SignaturePublic;
}

export const SignatureUploadDialog: React.FC<SignatureUploadDialogProps> = ({
  open,
  onOpenChange,
  signature,
}) => {
  const isEditing = !!signature;
  const uploadMutation = useUploadSignature();
  const updateMutation = useUpdateSignature();
  const imageUrl = signature ? useSignatureImageUrl(signature.id) : '';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData | 'file', string>>>({});

  // Reset form when dialog opens/closes or signature changes
  useEffect(() => {
    if (open) {
      if (signature) {
        setFormData({
          name: signature.name,
          title: signature.title || '',
          isActive: signature.isActive,
        });
        setPreviewUrl(null);
      } else {
        setFormData(defaultFormData);
        setPreviewUrl(null);
      }
      setSelectedFile(null);
      setErrors({});
    }
  }, [open, signature]);

  // Clean up preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clean up previous preview
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    if (errors.file) {
      setErrors((prev) => ({ ...prev, file: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData | 'file', string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!isEditing && !selectedFile) {
      newErrors.file = 'Please select a signature image';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (isEditing && signature) {
      const updateData: UpdateSignatureRequest = {
        name: formData.name,
        title: formData.title || undefined,
        isActive: formData.isActive,
      };

      await updateMutation.mutateAsync({ id: signature.id, data: updateData });
    } else if (selectedFile) {
      await uploadMutation.mutateAsync({
        file: selectedFile,
        name: formData.name,
        title: formData.title || undefined,
      });
    }

    onOpenChange(false);
  };

  const getInputClassName = (field: keyof FormData | 'file') =>
    cn(errors[field] && 'border-red-500 focus:border-red-500 focus:ring-red-500');

  const isPending = uploadMutation.isPending || updateMutation.isPending;

  // Determine the image to show
  const displayImage = previewUrl || (isEditing ? imageUrl : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Signature' : 'Upload Signature'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the signature details.'
              : 'Upload a signature image for use on cheques.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Signature Preview/Upload */}
          <div className="space-y-3">
            <Label>Signature Image {!isEditing && <span className="text-red-500">*</span>}</Label>
            <div className="flex flex-col items-center gap-4">
              <div
                className={cn(
                  'flex h-32 w-full items-center justify-center rounded-md border bg-white',
                  errors.file && 'border-red-500',
                )}
              >
                {displayImage ? (
                  <img
                    src={displayImage}
                    alt="Signature preview"
                    className="max-h-28 max-w-full object-contain"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">No signature selected</p>
                )}
              </div>
              <div className="flex w-full items-center gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className={cn('flex-1', getInputClassName('file'))}
                />
              </div>
              {errors.file && <p className="w-full text-sm text-red-600">{errors.file}</p>}
              <p className="text-xs text-muted-foreground">
                Recommended: PNG with transparent background, 400x100 pixels
              </p>
            </div>
          </div>

          {/* Signer Name */}
          <div className="space-y-1.5">
            <Label htmlFor="name">
              Signer Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="John Smith"
              className={getInputClassName('name')}
            />
            {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="title">Title (optional)</Label>
            <Input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="CEO"
              className={getInputClassName('title')}
            />
          </div>

          {/* Active Status (only for editing) */}
          {isEditing && (
            <div className="space-y-1.5">
              <Label htmlFor="isActive">Status</Label>
              <Select
                value={formData.isActive ? 'active' : 'inactive'}
                onValueChange={(v) => handleChange('isActive', v === 'active')}
              >
                <SelectTrigger id="isActive">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Upload Signature'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

SignatureUploadDialog.displayName = 'SignatureUploadDialog';
